/* eslint-disable react/prop-types */
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import Paper from '@base/Paper/Paper';
import H3 from '@base/H3/H3';
import H4 from '@base/H4/H4';
import H5 from '@base/H5/H5';
import { Spacings, Fonts, breakpoints, mediaQuery } from '@styles/index';
import { useState, useEffect } from 'react';
import Dialog from '@comp/Dialog/Dialog';
import PlayCircleBig from '@icon/PlayCircleBig';
import Thumbnail from '@base/Thumbnail/Thumbnail';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { segment } from '@actions/analytics';
import useWindowSize from '@hooks/useWindowSize';
import { useSelector } from 'react-redux';
import { getIsLoggedIn } from '@selectors/auth';
import NextImage from '@base/Image';
import {
  ONBOARDING_VIDEO_TESTIMONIAL_PLAYED,
  ONBOARDING_VIDEO_TESTIMONIAL_IMPRESSION
} from '@cont/Onboarding/constants';
import CardCarousel from './CardCarousel';

const QuoteImage = 'open-quote.svg';
const PlusImage = 'pitch-assist/ic_plus.svg';
const IconicImage = 'pitch-assist/ic_iconic.svg';
const SecWrapper = styled.section`
  position: relative;
  width: 1376px;
  margin: 160px auto 100px;
  padding-bottom: 60px;
  overflow: hidden;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    width: 728px;
    margin: 128px auto 60px;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin: 88px 0 48px;
    padding: 0 0 ${Spacings.SPACING_10B};
    width: 100%;
  }
`;

const Title = styled(H3)`
  font-size: ${Spacings.SPACING_10B};
  text-align: center;
  margin-bottom: ${Spacings.SPACING_16B};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    font-size: ${Spacings.SPACING_7B};
    padding: 0 ${Spacings.SPACING_4B};
  }
`;

const SubscriptionLabel = styled.div`
  font-size: ${Fonts.SMALL};
  font-weight: ${Fonts.SEMIBOLD};
  color: ${($type) => $type && $type.$type};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    font-size: ${Spacings.SPACING_3B};
  }
`;

const CardContainer = styled(Paper)`
  margin-right: ${Spacings.SPACING_4B};
  border-radius: ${Spacings.SPACING_2B};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  border: 1px solid var(--color-shadow);
  box-shadow: ${boxShadowGenerator()};
  background: ${({ $bgColor }) =>
    `linear-gradient(179.92deg,${$bgColor} 0.07%, var(--color-base-1) 40%)`};
  padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_8B} ${Spacings.SPACING_10B};
  width: 326px;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    width: 286px;
    height: 395px;
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_6B} 32px;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 900px;
  height: 506px;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    width: 704px;
    height: 396px;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 100vw;
    height: calc(100vw * 9 / 16);
  }
`;

const CardThumbnail = styled(Thumbnail)`
  &.thumbnail {
    width: 160px;
    height: 160px;
    border-radius: 50%;
    ${mediaQuery.uptoTablet} {
      width: 130px;
      height: 130px;
    }
  }
`;

const CardTop = styled.div`
  position: relative;
`;

const PlayButton = styled(PlayCircleBig)`
  position: absolute;
  border: 3px solid var(--color-base-1);
  box-shadow: ${boxShadowGenerator()};
  top: 127px;
  left: 49.39px;
  background: var(--color-base-1);
  border-radius: 50%;
  cursor: pointer;
  width: ${Spacings.SPACING_15B};
  height: ${Spacings.SPACING_15B};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    width: ${Spacings.SPACING_13B};
    height: ${Spacings.SPACING_13B};
    top: 97px;
    left: 39px;
  }
`;

const CardDescription = styled.div`
  position: relative;
  padding-top: ${Spacings.SPACING_17B};
  ${mediaQuery.uptoTablet} {
    padding-top: 51px;
  }
`;

const QuoteImageWrapper = styled.div`
  position: absolute;
  top: 30px;
  left: 4px;
  z-index: 0;
  ${mediaQuery.uptoTablet} {
    top: 22px;
    img {
      height: 24px;
      width: 24px;
    }
  }
`;

const CardStatement = styled(H4)`
  font-weight: ${Fonts.SEMIBOLD};
  line-height: 130%;
  height: 78px;
  text-align: center;
`;

const SubscriptionInfo = styled.div`
  margin-top: ${Spacings.SPACING_12B};
  display: flex;
  align-items: center;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    margin-top: 33px;
  }
`;

const SubscriptionIconWrapper = styled.div`
  margin-right: ${Spacings.SPACING_2B};
  width: 30px;
  height: 30px;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    width: 18px;
    height: 18px;
  }
`;

const CardFooter = styled(H5)`
  line-height: 130%;
  font-weight: ${Fonts.REGULAR};
  color: var(--color-text-primary);
  text-align: center;
  margin-top: ${Spacings.SPACING_3B};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    font-size: ${Fonts.SMALL};
    margin-top: 11px;
  }
`;

const ResponsiveIframe = styled.iframe`
  width: 100%;
  height: 100%;
`;

const CardWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const Card = ({ review, goalName, sendTestimonialPlayedEvent }) => {
  const {
    reviewer,
    statement,
    bgColor,
    subscriptionType,
    uid,
    videoUrl: video_url
  } = review;
  const { displayImage, displayName } = reviewer;
  const footerText = `${displayName} • ${goalName}`;
  const [showDialog, setDialogState] = useState(false);
  const [videoUrl, setVideoURL] = useState(null);
  const subscriptionInfo = {
    1: {
      icon: PlusImage,
      label: 'PLUS LEARNER',
      color: 'var(--color-text-secondary)'
    },
    5: {
      icon: IconicImage,
      label: 'ICONIC LEARNER',
      color: 'var(--color-i-orange-1)'
    }
  };

  const handlePlay = (url) => {
    setDialogState(true);
    setVideoURL(url);
    sendTestimonialPlayedEvent(displayName, uid);
  };

  return (
    <>
      <CardContainer $bgColor={bgColor}>
        <CardTop>
          <CardThumbnail
            imgUrl={displayImage}
            orientation="vertical"
            className="thumbnail"
          />
          <PlayButton
            color="var(--color-i-green)"
            onClick={() => handlePlay(video_url)}
          />
        </CardTop>
        <CardDescription>
          <QuoteImageWrapper>
            <NextImage src={QuoteImage} height={30} width={30} alt="quote" />
          </QuoteImageWrapper>
          {!!statement && (
            <CardStatement lineClamp={3}>
              {statement.substring(0, 80)}
            </CardStatement>
          )}
        </CardDescription>
        <SubscriptionInfo>
          <SubscriptionIconWrapper>
            <NextImage
              src={subscriptionInfo[subscriptionType].icon}
              alt="icon-type"
              width={30}
              height={30}
            />
          </SubscriptionIconWrapper>
          <SubscriptionLabel $type={subscriptionInfo[subscriptionType].color}>
            {subscriptionInfo[subscriptionType].label}
          </SubscriptionLabel>
        </SubscriptionInfo>
        {!!footerText && (
          <CardFooter lineClamp={1}>{footerText.substring(0, 36)}</CardFooter>
        )}
      </CardContainer>
      <Dialog
        center
        onClose={() => setDialogState(false)}
        dialogState={showDialog}
        showButtons
        maxWidth
        fullWidth
        mWebFullWidth
      >
        <VideoContainer>
          <ResponsiveIframe
            src={videoUrl}
            frameBorder="0"
            allow="autoplay; encrypted-media"
            allowFullScreen
            title="video"
          />
        </VideoContainer>
      </Dialog>
    </>
  );
};
Card.propTypes = {
  sendTestimonialPlayedEvent: PropTypes.func
};
Card.defaultProps = {
  sendTestimonialPlayedEvent: () => {}
};

const VideoTestimonialsSection = ({ block, goalDetails = {}, href, as }) => {
  const { data } = block;
  const { name, uid } = goalDetails;
  const label =
    block?.extraBlockInfo?.header || 'Hear from our learners & their parents';
  const isLoggedIn = useSelector(getIsLoggedIn);
  const { isMobile, isTablet } = useWindowSize();
  useEffect(() => {
    if (data && data.length) {
      segment.track(ONBOARDING_VIDEO_TESTIMONIAL_IMPRESSION, {
        goal_uid: uid,
        goal_name: name,
        is_logged_in: isLoggedIn,
        last_primary_source_section: 'Goal Page'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendTestimonialPlayedEvent = (displayName = '', videoUid = '') => {
    segment.track(ONBOARDING_VIDEO_TESTIMONIAL_PLAYED, {
      goal_uid: uid,
      goal_name: name,
      is_logged_in: isLoggedIn,
      last_primary_source_section: 'Goal Page',
      video_testimonial_uid: videoUid,
      testimonial_video_title: displayName
    });
  };

  const renderCards = () => {
    return data.map((review, index) => (
      <Card
        key={String(index)}
        review={review}
        goalName={name}
        index={index}
        sendTestimonialPlayedEvent={sendTestimonialPlayedEvent}
      />
    ));
  };

  return (
    <SecWrapper>
      <Title>{label}</Title>
      {data.length <= 3 && !isMobile && !isTablet ? (
        <CardWrapper>{renderCards()}</CardWrapper>
      ) : (
        <CardCarousel
          step={2}
          href={href}
          as={as}
          type="dummy"
          rightShadowDivHeight="550px"
        >
          {renderCards()}
        </CardCarousel>
      )}
    </SecWrapper>
  );
};

VideoTestimonialsSection.propTypes = {
  block: PropTypes.objectOf(PropTypes.any).isRequired,
  goalDetails: PropTypes.objectOf(PropTypes.any).isRequired
};
export default VideoTestimonialsSection;
